import {
  AS400_CDB_COLUMN,
  as400DivisionCode,
  as400DivisionExportsPerBranch,
} from '@erp/rpc';
import {
  ITEM_ADD_ITEM_FIELD,
  ITEM_ADD_LANGUAGE_FIELD,
  newRowKey,
  normalizePartNumber,
  type ItemAddLine,
  type ItemAddLookup,
} from '@erp/item-master-rules';
import { asCellText, persistedCellValue } from './as400ExportParts';
import type { DivisionOption } from './useDivisionSelection';

// The rule side (constants, the lookup's shape, itemAddRequiredRule) lives in
// @erp/item-master-rules so the server checks it too; the grid's lookup
// building stays here.
export {
  ITEM_ADD_ITEM_FIELD,
  ITEM_ADD_LANGUAGE_FIELD,
  SHELF_LIFE_LANGUAGES,
  itemAddHasLanguageSlots,
  itemAddPresenceProblem,
  itemAddRequiredRule,
  languageSlot,
  normalizePartNumber,
  type ItemAddLine,
  type ItemAddLookup,
} from '@erp/item-master-rules';

/**
 * The upload file a CDB lands in: its division's code, plus the location for
 * a division whose upload runs per branch (RD). An ingest row's CDB carries
 * its location (`QER`) while an authored row's names the division alone
 * (`QE`), so both forms resolve to `QE` here. The longest code wins, as in
 * parseNewRowCdb. A CDB that names no division keys as itself.
 */
export function itemAddRecordScope(
  divisionCodes: readonly string[],
  cdb: string | null | undefined,
): string {
  const key = (cdb ?? '').trim().toUpperCase();
  let code = '';
  for (const candidate of divisionCodes) {
    if (candidate.length > code.length && key.startsWith(candidate)) {
      code = candidate;
    }
  }
  if (code === '') return key;
  return as400DivisionExportsPerBranch(code) ? key : code;
}

/**
 * Item Add lines by part number, narrowed to the asking row's upload file:
 * AS/400 keys Item Add by company, division and location, so another
 * division's text doesn't load with this one's item. A line with no CDB yet
 * (an unsaved draft) counts for every division, and a row whose CDB is unknown
 * sees every line of its part.
 */
export function itemAddsLookup(
  lines: readonly ItemAddLine[],
  divisionCodes: readonly string[] = [],
): ItemAddLookup {
  const byItem = new Map<string, ItemAddLine[]>();
  for (const line of lines) {
    const key = normalizePartNumber(line.itemNumber);
    if (!key) continue;
    const list = byItem.get(key);
    if (list) list.push(line);
    else byItem.set(key, [line]);
  }
  return (itemNumber, cdb) => {
    const all = byItem.get(normalizePartNumber(itemNumber)) ?? [];
    const scope = itemAddRecordScope(divisionCodes, cdb);
    if (scope === '') return all;
    return all.filter((line) => {
      const lineScope = itemAddRecordScope(divisionCodes, line.cdb);
      return lineScope === '' || lineScope === scope;
    });
  };
}

export function itemAddLineFromValues(
  values: Record<string, string | null | undefined>,
): ItemAddLine | null {
  const itemNumber = asCellText(values[ITEM_ADD_ITEM_FIELD]);
  if (!itemNumber) return null;
  return {
    itemNumber,
    language: asCellText(values[ITEM_ADD_LANGUAGE_FIELD]),
    cdb: asCellText(values[AS400_CDB_COLUMN]) ?? undefined,
  };
}

export function overlayItemAddValues(
  base: Record<string, string | null | undefined>,
  pending?: Record<string, string | null>,
): Record<string, string | null | undefined> {
  return pending ? { ...base, ...pending } : base;
}

export function takePendingItemAddLine(
  rowKey: string,
  values: Record<string, string | null | undefined>,
  pendingByKey: Map<string, Record<string, string | null>>,
): ItemAddLine | null {
  const line = itemAddLineFromValues(
    overlayItemAddValues(values, pendingByKey.get(rowKey)),
  );
  pendingByKey.delete(rowKey);
  return line;
}

export type OverlayCollectInput = {
  rows?: readonly {
    key: string;
    values: Record<string, string | null>;
    stagedValues?: Record<string, unknown> | null;
  }[];
  newRows?: readonly {
    id: string;
    values: Record<string, string | null>;
  }[];
  pending?: Iterable<{
    rowKey: string;
    values: Record<string, string | null>;
  }>;
};

export function collectOverlayLines<T>(
  input: OverlayCollectInput,
  lineFromValues: (
    values: Record<string, string | null | undefined>,
  ) => T | null,
  persistedFields: readonly string[],
): T[] {
  const pendingByKey = new Map<string, Record<string, string | null>>();
  for (const edit of input.pending ?? []) {
    pendingByKey.set(edit.rowKey, {
      ...pendingByKey.get(edit.rowKey),
      ...edit.values,
    });
  }
  const take = (
    rowKey: string,
    values: Record<string, string | null | undefined>,
  ): T | null => {
    const line = lineFromValues(
      overlayItemAddValues(values, pendingByKey.get(rowKey)),
    );
    pendingByKey.delete(rowKey);
    return line;
  };
  return [
    ...(input.rows ?? []).flatMap((row) => {
      const values: Record<string, string | null | undefined> = {};
      for (const field of persistedFields) {
        values[field] = persistedCellValue(row, field);
      }
      const line = take(row.key, values);
      return line ? [line] : [];
    }),
    ...(input.newRows ?? []).flatMap((row) => {
      const line = take(newRowKey(row.id), row.values);
      return line ? [line] : [];
    }),
    ...[...pendingByKey.values()].flatMap((values) => {
      const line = lineFromValues(values);
      return line ? [line] : [];
    }),
  ];
}

export function collectItemAddLines(input: OverlayCollectInput): ItemAddLine[] {
  return collectOverlayLines(input, itemAddLineFromValues, [
    ITEM_ADD_ITEM_FIELD,
    ITEM_ADD_LANGUAGE_FIELD,
    AS400_CDB_COLUMN,
  ]);
}

/**
 * An authored row stores its division and location, not a CDB: give it the
 * one the grid shows it under (see cdbOf in As400ExportEditableSection), so
 * its Item Add lines key to that division.
 */
function newRowsWithCdb(
  newRows: ItemAddLookupInputs['newRows'],
  divisions: readonly DivisionOption[],
): ItemAddLookupInputs['newRows'] {
  return newRows?.map((row) => {
    const division = divisions.find(
      (candidate) => candidate.id === row.dataSourceId,
    );
    if (!division || row.values[AS400_CDB_COLUMN]) return row;
    return {
      ...row,
      values: {
        ...row.values,
        [AS400_CDB_COLUMN]: `${as400DivisionCode(division.title)}${row.branch ?? ''}`,
      },
    };
  });
}

export function pendingScopesWithPrefix<T>(
  byScope: ReadonlyMap<string, T>,
  scopePrefix: string,
): T[] {
  return [...byScope].flatMap(([scopeKey, scope]) =>
    scopeKey.startsWith(scopePrefix) ? [scope] : [],
  );
}

export interface ItemAddLookupInputs {
  ready: boolean;
  rows?: Parameters<typeof collectItemAddLines>[0]['rows'];
  newRows?: readonly {
    id: string;
    values: Record<string, string | null>;
    dataSourceId?: string;
    branch?: string | null;
  }[];
  pendingScopes: readonly ReadonlyMap<
    string,
    { rowKey: string; values: Record<string, string | null> }
  >[];
  /** The review's divisions: what a CDB resolves against (itemAddRecordScope). */
  divisions?: readonly DivisionOption[];
}

export function worksheetLookupInputs<
  T extends ReadonlyMap<
    string,
    { rowKey: string; values: Record<string, string | null> }
  >,
>(
  ready: boolean,
  rows: ItemAddLookupInputs['rows'],
  newRows: ItemAddLookupInputs['newRows'],
  scopePrefix: string | undefined,
  pendingByScope: ReadonlyMap<string, T>,
  divisions?: readonly DivisionOption[],
): ItemAddLookupInputs {
  return {
    ready,
    rows,
    newRows,
    pendingScopes: scopePrefix
      ? pendingScopesWithPrefix(pendingByScope, scopePrefix)
      : [],
    divisions,
  };
}

export function itemAddLookupFor(
  inputs: ItemAddLookupInputs,
): ItemAddLookup | undefined {
  if (!inputs.ready) return undefined;
  const divisions = inputs.divisions ?? [];
  return itemAddsLookup(
    collectItemAddLines({
      rows: inputs.rows,
      newRows: newRowsWithCdb(inputs.newRows, divisions),
      pending: inputs.pendingScopes.flatMap((scope) => [...scope.values()]),
    }),
    divisions.map((division) => as400DivisionCode(division.title)),
  );
}

// By content: the division list is refetched (and defaulted to a fresh []
// while loading), and a new identity alone must not rebuild the lookup.
function sameDivisions(
  a: readonly DivisionOption[] | undefined,
  b: readonly DivisionOption[] | undefined,
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  return (
    left.length === right.length &&
    left.every(
      (division, index) =>
        division.id === right[index]?.id &&
        division.title === right[index]?.title,
    )
  );
}

export function sameItemAddInputs(
  a: ItemAddLookupInputs,
  b: ItemAddLookupInputs,
): boolean {
  return (
    a.ready === b.ready &&
    a.rows === b.rows &&
    a.newRows === b.newRows &&
    a.pendingScopes.length === b.pendingScopes.length &&
    a.pendingScopes.every((scope, index) => scope === b.pendingScopes[index]) &&
    sameDivisions(a.divisions, b.divisions)
  );
}
